import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { usePartner } from "../context/PartnerContext";
import { BrandLogo } from "../components/BrandLogo";

export default function PartnerRegister() {
  const { register } = usePartner();
  const [form, setForm] = useState({
    nombre: "", apellido: "", email: "", password: "", telefono: "",
    rut: "", empresa: "", rut_empresa: "", n_comunidades_admin: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const canSubmit = form.nombre && form.apellido && form.email && form.password.length >= 6 && form.telefono;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const payload = { ...form, n_comunidades_admin: parseInt(form.n_comunidades_admin) || 0 };
    const res = await register(payload);
    setBusy(false);
    if (res.ok) {
      setDone(true);
      toast.success("¡Solicitud enviada!", { description: "Recibirás un email cuando tu cuenta sea aprobada." });
    } else setErr(res.error);
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-md text-center fade-in-up">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "rgba(46,125,50,0.12)" }}>
            <CheckCircle2 size={40} color="#2E7D32" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3" style={{ fontFamily: "Manrope" }}>¡Solicitud recibida!</h1>
          <p className="mb-6 text-lg" style={{ color: "var(--text-muted)" }}>
            Un asesor revisará tu cuenta y te enviaremos un email de confirmación en menos de <strong>24 horas hábiles</strong>.
          </p>
          <Link to="/" className="btn-primary">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-5" style={{ background: "var(--bg-soft)" }}>
      <div className="md:col-span-2 hidden md:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "var(--brand-navy)" }}>
        <Link to="/" className="relative z-10">
          <BrandLogo size={44} variant="light" subline="Programa Partners" iconBg="rgba(255,255,255,0.04)"/>
        </Link>

        <div className="text-white relative z-10">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: "var(--brand-accent)" }}>Únete al programa</div>
          <h1 className="text-3xl font-extrabold tracking-tight leading-tight" style={{ fontFamily: "Manrope" }}>
            12% sobre prima anual.<br />
            <span className="opacity-75 font-light italic">Cero papeleo.</span>
          </h1>
          <p className="mt-4 opacity-75">
            Completa el formulario y te aprobamos en 24 hrs hábiles.
          </p>
        </div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full opacity-15" style={{ background: "var(--brand-accent)" }}></div>
      </div>

      <div className="md:col-span-3 flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-xl" data-testid="partner-register-form">
          <h2 className="text-3xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "Manrope" }}>Crear cuenta partner</h2>
          <p className="text-sm mb-7" style={{ color: "var(--text-muted)" }}>Registro gratuito · Sin permanencia · Aprobación en 24h hábiles</p>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nombre *"><input className="field-input" value={form.nombre} onChange={(e) => set("nombre", e.target.value)} required data-testid="reg-nombre"/></Field>
            <Field label="Apellido *"><input className="field-input" value={form.apellido} onChange={(e) => set("apellido", e.target.value)} required data-testid="reg-apellido"/></Field>
            <Field label="Email *"><input type="email" className="field-input" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="reg-email"/></Field>
            <Field label="Teléfono *"><input type="tel" className="field-input" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="+56 9 ..." required data-testid="reg-telefono"/></Field>
            <Field label="Contraseña *" hint="Mínimo 6 caracteres"><input type="password" className="field-input" value={form.password} onChange={(e) => set("password", e.target.value)} minLength={6} required data-testid="reg-password"/></Field>
            <Field label="RUT personal"><input className="field-input" value={form.rut} onChange={(e) => set("rut", e.target.value)} placeholder="12.345.678-9"/></Field>
            <Field label="Empresa administradora"><input className="field-input" value={form.empresa} onChange={(e) => set("empresa", e.target.value)} placeholder="Nombre o N/A si independiente"/></Field>
            <Field label="RUT empresa"><input className="field-input" value={form.rut_empresa} onChange={(e) => set("rut_empresa", e.target.value)}/></Field>
            <Field label="N° comunidades que administras"><input type="number" min="0" className="field-input" value={form.n_comunidades_admin} onChange={(e) => set("n_comunidades_admin", e.target.value)} placeholder="0"/></Field>
          </div>

          {err && <div className="mt-4 p-3 rounded-md text-sm border" style={{ borderColor: "rgba(211,47,47,0.25)", background: "rgba(211,47,47,0.05)", color: "#9a1919" }} data-testid="reg-error">{err}</div>}

          <p className="text-[12px] mt-5" style={{ color: "var(--text-muted)" }}>
            Al registrarte aceptas nuestra política de privacidad (Ley 19.628) y los términos del programa de comisiones.
          </p>

          <button type="submit" className="btn-primary w-full justify-center mt-5" disabled={!canSubmit || busy} data-testid="reg-submit">
            {busy ? "Enviando..." : "Crear cuenta partner"}
          </button>

          <div className="mt-5 flex justify-between text-sm">
            <Link to="/partner/login" className="nav-link" style={{ color: "var(--text-muted)" }}>Ya tengo cuenta</Link>
            <Link to="/" className="inline-flex items-center gap-1 nav-link" style={{ color: "var(--text-muted)" }}>
              <ArrowLeft size={13}/> Volver al sitio
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children, hint }) => (
  <div>
    <label className="field-label">{label}</label>
    {children}
    {hint && <span className="field-hint">{hint}</span>}
  </div>
);
